import {
  AccountInfo,
  AuthenticationResult,
  EventMessage,
  EventType,
  InteractionRequiredAuthError,
  LogLevel,
  PublicClientApplication,
} from '@azure/msal-browser';
import { authConfig } from '../config/auth';

const env = (window as any).env || {};
const authDebug = (env.REACT_APP_AUTH_DEBUG ?? process.env.REACT_APP_AUTH_DEBUG ?? 'false').toLowerCase() === 'true';
export const authDebugLog = (...args: unknown[]) => {
  if (authDebug) {
    console.log('[AuthDebug]', ...args);
  }
};

const authority = authConfig.tenantId
  ? `https://login.microsoftonline.com/${authConfig.tenantId}`
  : undefined;

const interactionStartEvents = new Set<EventType>([
  EventType.LOGIN_START,
  EventType.ACQUIRE_TOKEN_START,
  EventType.HANDLE_REDIRECT_START,
  EventType.SSO_SILENT_START,
]);
const interactionEndEvents = new Set<EventType>([
  EventType.LOGIN_SUCCESS,
  EventType.LOGIN_FAILURE,
  EventType.ACQUIRE_TOKEN_SUCCESS,
  EventType.ACQUIRE_TOKEN_FAILURE,
  EventType.HANDLE_REDIRECT_END,
  EventType.SSO_SILENT_SUCCESS,
  EventType.SSO_SILENT_FAILURE,
]);

let interactionInProgress = false;

const setInteractionStatus = (isInProgress: boolean, eventType?: EventType) => {
  interactionInProgress = isInProgress;
  authDebugLog('Interaction status changed', { eventType, interactionInProgress });
};

let msalInstance: PublicClientApplication | null = null;
let initializationPromise: Promise<void> | null = null;
let initialized = false;

export const getMsalInstance = (): PublicClientApplication | null => {
  if (msalInstance) {
    return msalInstance;
  }

  if (authConfig.enabled) {
    authDebugLog('Creating new MSAL instance...');
    try {
      msalInstance = new PublicClientApplication({
        auth: {
          clientId: authConfig.clientId,
          authority,
          redirectUri: authConfig.redirectUri,
          postLogoutRedirectUri: authConfig.redirectUri,
          navigateToLoginRequestUrl: false,
        },
        cache: {
          cacheLocation: 'localStorage',
          storeAuthStateInCookie: false,
        },
        system: {
          loggerOptions: {
            loggerCallback: (level, message) => {
              // Logging disabled to reduce noise
            },
          },
        },
      });

      msalInstance.addEventCallback((event?: EventMessage) => {
        if (!event) {
          return;
        }
        if (interactionStartEvents.has(event.eventType)) {
          setInteractionStatus(true, event.eventType);
        }
        if (interactionEndEvents.has(event.eventType)) {
          setInteractionStatus(false, event.eventType);
        }
      });

      authDebugLog('MSAL instance created successfully');
    } catch (error) {
      console.error('[AuthService] Failed to create MSAL instance', error);
      msalInstance = null;
    }
  } else {
    authDebugLog('MSAL instance generation skipped (Auth disabled)');
  }

  return msalInstance;
};

const ensureInitialized = async () => {
  const instance = getMsalInstance();
  if (!instance || initialized) {
    return;
  }

  // Check config at runtime when window.env is available
  if (authConfig.missingConfig) {
    console.warn('[Auth] Authentication is enabled but Azure AD environment variables are missing.');
  }

  if (!initializationPromise) {
    authDebugLog('Initializing MSAL instance with config:', {
      authority: authConfig.tenantId ? `https://login.microsoftonline.com/${authConfig.tenantId}` : 'undefined',
      clientId: authConfig.clientId ? '***' : 'missing',
      redirectUri: authConfig.redirectUri,
      enabled: authConfig.enabled
    });
    initializationPromise = instance
      .initialize()
      .then(() => {
        initialized = true;
        authDebugLog('MSAL initialized successfully');
      })
      .catch((error) => {
        initializationPromise = null;
        authDebugLog('MSAL initialization failed', error);
        throw error;
      });
  }
  await initializationPromise;
};

const loginRequest = {
  scopes: authConfig.scopes,
  redirectUri: authConfig.redirectUri,
};

const ensureAccount = async (): Promise<AccountInfo | null> => {
  const instance = getMsalInstance();
  if (!instance) {
    return null;
  }
  await ensureInitialized();
  let account = instance.getActiveAccount();
  if (!account) {
    const existingAccounts = instance.getAllAccounts();
    if (existingAccounts.length > 0) {
      account = existingAccounts[0];
      instance.setActiveAccount(account);
    }
  }
  return account ?? null;
};

export const isAuthEnabled = () => authConfig.enabled;

export const initializeAuth = async (): Promise<AccountInfo | null> => ensureAccount();

export const getActiveAccount = (): AccountInfo | null => {
  const instance = getMsalInstance();
  if (!instance || !initialized) {
    return null;
  }

  try {
    return instance.getActiveAccount();
  } catch (error) {
    authDebugLog('Skipping getActiveAccount before MSAL init is ready', error);
    return null;
  }
};

export const getUserRoles = (): string[] => {
  const account = getActiveAccount();
  if (!account || !account.idTokenClaims) return [];
  return (account.idTokenClaims.roles as string[]) || [];
};

const ELEVATED_ADMIN_ROLES = [
  'APMT.CDH.SelfService.Admin',
  'APMT.CDH.SelfService.SuperAdmin',
  'APMT.CDH.SelfService.Super.Admin',
  'APMT.CDH.SelfService.Super Admin',
];

export const hasElevatedAdminAccess = (roles: string[] = getUserRoles()): boolean => {
  const roleSet = new Set(ELEVATED_ADMIN_ROLES.map((r) => r.toLowerCase()));
  return roles.some((role) => {
    const normalized = (role || '').toLowerCase();
    return roleSet.has(normalized);
  });
};

const guardInteractiveRequest = (action: 'login' | 'token') => {
  if (interactionInProgress) {
    authDebugLog(`Skipping ${action} interaction because another action is active`);
    return false;
  }
  setInteractionStatus(true);
  return true;
};

export const login = async (): Promise<void> => {
  const instance = getMsalInstance();
  if (!instance) {
    return;
  }
  await ensureInitialized();
  if (!guardInteractiveRequest('login')) {
    return;
  }
  authDebugLog('Starting login popup', { scopes: loginRequest.scopes });
  try {
    const result = await instance.loginPopup(loginRequest);
    if (result.account) {
      instance.setActiveAccount(result.account);
    }
  } catch (error) {
    authDebugLog('Login failed', error);
    throw error;
  }
};

export const logout = async (): Promise<void> => {
  const instance = getMsalInstance();
  if (!instance) {
    return;
  }
  await ensureInitialized();

  // If an interaction is in progress, we should try to clear it or wait, 
  // but for logout, we often just want to force it.
  // However, MSAL blocks logoutPopup if interaction is in progress.
  if (interactionInProgress) {
    authDebugLog('Interaction in progress during logout, attempting to clear...');
    // We can't strictly "clear" it internally in MSAL without a hack, 
    // but we can ensure our local state is reset.
    // Realistically, if a popup is open, logout might fail. 
    // We should at least try to proceed.
  }

  authDebugLog('Starting logout popup');
  try {
    // Clear all local caches to prevent stale data (e.g. dashboard stats)
    localStorage.clear();
    sessionStorage.clear();
    
    await instance.logoutPopup({
      mainWindowRedirectUri: authConfig.redirectUri,
    });
  } catch (error: any) {
    if (error.errorCode === 'interaction_in_progress') {
      authDebugLog('Logout interaction conflict, forcing redirect logout as fallback');
      // Fallback to redirect if popup fails due to interaction
      await instance.logoutRedirect({
        postLogoutRedirectUri: authConfig.redirectUri
      });
    } else {
      authDebugLog('Logout failed', error);
    }
  }
};

/*
 * Token Request Deduplication (Promise Singleton)
 * Ensures only one token request (silent or popup) happens at a time.
 */
let accessTokenPromise: Promise<string | null> | null = null;

export const acquireAccessToken = async (
  forceRefresh: boolean = false,
  allowPopup: boolean = true
): Promise<string | null> => {
  // Return existing promise if request is already in flight
  if (accessTokenPromise) {
    authDebugLog('Reusing existing token request promise');
    return accessTokenPromise;
  }

  // Create a new promise for this request cycle
  accessTokenPromise = (async () => {
    const instance = getMsalInstance();
    if (!instance) {
      return null;
    }

    try {
      await ensureInitialized();
      await ensureAccount();
      const account = instance.getActiveAccount();

      if (!account) {
        authDebugLog('No account active, cannot acquire token');
        return null;
      }

      try {
        const response = await instance.acquireTokenSilent({
          ...loginRequest,
          account,
          forceRefresh,
        });
        authDebugLog('Acquired token silently');
        return response.accessToken;
      } catch (error) {
        if (error instanceof InteractionRequiredAuthError) {
          authDebugLog('Silent token failed, requires interaction');
          if (!allowPopup) {
            authDebugLog('Interactive popup not allowed for this request');
            return null;
          }
          if (!guardInteractiveRequest('token')) {
            authDebugLog('Interaction guarded, skipping popup');
            return null;
          }

          try {
            const popupResponse = await instance.acquireTokenPopup({
              ...loginRequest,
              account
            });
            return popupResponse.accessToken;
          } catch (popupError) {
            console.error('Popup failed', popupError);
            setInteractionStatus(false);
            return null;
          }
        }
        console.error('Silent token acquisition failed', error);
        return null;
      }
    } finally {
      // Clear promise so next call starts fresh
      accessTokenPromise = null;
    }
  })();

  return accessTokenPromise;
};

let reauthenticationPromise: Promise<void> | null = null;

export const requireReauthentication = async () => {
  // Deduplicate concurrent re-auth calls so multiple 401 responses
  // never open more than one login popup at a time.
  if (reauthenticationPromise) {
    authDebugLog('Re-authentication already in progress, waiting for existing attempt');
    return reauthenticationPromise;
  }
  authDebugLog('Re-authentication requested');
  reauthenticationPromise = login().finally(() => {
    reauthenticationPromise = null;
  });
  return reauthenticationPromise;
};
